import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, FileSpreadsheet, Code, Webhook, CheckCircle, Download, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function IntegrationsContent() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [apiKey, setApiKey] = useState('');
  
  // Project Mapping state
  const [mappings, setMappings] = useState([]);
  const [projects, setProjects] = useState([]);
  const [newMapping, setNewMapping] = useState({ external_project_name: '', crm_project_id: '' });
  const [loadingMappings, setLoadingMappings] = useState(false);

  React.useEffect(() => {
    const token = localStorage.getItem('token');
    setApiKey(token);
    loadMappings();
    loadProjects();
  }, []);

  const loadMappings = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/project-mappings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMappings(response.data);
    } catch (error) {
      console.error('Failed to load mappings');
    }
  };

  const loadProjects = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to load projects');
    }
  };

  const handleAddMapping = async () => {
    if (!newMapping.external_project_name || !newMapping.crm_project_id) {
      toast.error('Please fill all fields');
      return;
    }

    setLoadingMappings(true);
    const token = localStorage.getItem('token');

    try {
      await axios.post(`${API}/project-mappings`, newMapping, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping added successfully');
      setNewMapping({ external_project_name: '', crm_project_id: '' });
      loadMappings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add mapping');
    } finally {
      setLoadingMappings(false);
    }
  };

  const handleDeleteMapping = async (mappingId) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/project-mappings/${mappingId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Mapping deleted');
      loadMappings();
    } catch (error) {
      toast.error('Failed to delete mapping');
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileType = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(fileType)) {
        toast.error('Please upload Excel (.xlsx, .xls) or CSV file');
        return;
      }
      setFile(selectedFile);
    }
  };

  const handleBulkUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    setUploadResults(null);

    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('token');
    try {
      const response = await axios.post(`${API}/leads/bulk-upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      setUploadResults(response.data);
      toast.success(`Successfully uploaded ${response.data.success_count} leads`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload leads');
    } finally {
      setUploading(false);
    }
  };

  const downloadRejectedLeads = async (uploadId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/leads/bulk-upload/download-rejected/${uploadId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rejected_leads_${uploadId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Rejected leads exported successfully');
    } catch (error) {
      toast.error('Failed to export rejected leads');
    }
  };

  const downloadTemplate = () => {
    const csvContent = `customer_name,mobile,email,projects,source,sub_source,lead_status,dead_reason,comments,sales_rep_name,lead_date
John Doe,9876543210,john@example.com,"Aarambh,Solace",Facebook,Meta Campaign 1,New,,,Aarti Darji,01/11/2025
Jane Smith,9876543211,jane@example.com,Vistara,Website,Organic Search,Attempted-1,,Called twice - not answering,,15/11/2025
Raj Kumar,9876543212,raj@example.com,Samesh,99acres,,Site Visit Planned,,Site visit scheduled for 15th Nov,Meet Jadav,10/11/2025`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'lead_upload_template.csv';
    a.click();
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <div data-testid="integrations-content">
      <Tabs defaultValue="bulk-upload">
        <TabsList className="grid w-full grid-cols-4 gap-2 mb-6">
          <TabsTrigger value="bulk-upload" data-testid="bulk-upload-tab" className="text-sm">
            <FileSpreadsheet className="mr-1 h-4 w-4" />
            Bulk Upload
          </TabsTrigger>
          <TabsTrigger value="meta-ads" data-testid="meta-ads-tab" className="text-sm">
            <Webhook className="mr-1 h-4 w-4" />
            Meta Ads
          </TabsTrigger>
          <TabsTrigger value="api" data-testid="api-tab" className="text-sm">
            <Code className="mr-1 h-4 w-4" />
            API & Automation
          </TabsTrigger>
          <TabsTrigger value="project-mapping" data-testid="project-mapping-tab" className="text-sm">
            <Code className="mr-1 h-4 w-4" />
            Project Mapping
          </TabsTrigger>
        </TabsList>

          {/* Bulk Upload Tab */}
          <TabsContent value="bulk-upload">
            <Card data-testid="bulk-upload-card">
              <CardHeader>
                <CardTitle>Upload Leads from Excel/CSV</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <AlertDescription>
                    <div className="space-y-2">
                      <p className="font-medium">Required Columns:</p>
                      <ul className="text-sm space-y-1 ml-4 list-disc">
                        <li><strong>customer_name</strong> - Full name (required)</li>
                        <li><strong>mobile</strong> - Mobile number (required, unique, 7-15 digits)</li>
                        <li><strong>projects</strong> - Comma-separated project names (required)</li>
                        <li><strong>source</strong> - Lead source (required: Magicbricks, 99 Acres, Facebook, etc.)</li>
                      </ul>
                      <p className="font-medium mt-3">Optional Columns:</p>
                      <ul className="text-sm space-y-1 ml-4 list-disc">
                        <li><strong>email</strong> - Email address</li>
                        <li><strong>sub_source</strong> - Additional source info</li>
                        <li><strong>lead_status</strong> - Status (e.g., New, Attempted-1, Site Visit Planned, etc.)</li>
                        <li><strong>dead_reason</strong> - Reason for Lost status</li>
                        <li><strong>comments</strong> - Comments and activity notes</li>
                        <li><strong>sales_rep_name</strong> - Name of specific Sales Rep to assign (overrides round robin)</li>
                        <li><strong>lead_date</strong> - Custom lead creation date (format: DD-MM-YYYY or DD/MM/YYYY, e.g., 01-11-2025 = 1st November 2025)</li>
                      </ul>
                    </div>
                  </AlertDescription>
                </Alert>

                <div className="flex gap-3">
                  <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-button">
                    <Download className="mr-2 h-4 w-4" />
                    Download Template
                  </Button>
                </div>

                <div className="space-y-3">
                  <Label htmlFor="file-upload">Select File</Label>
                  <Input
                    id="file-upload"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileChange}
                    data-testid="file-input"
                  />
                  {file && (
                    <p className="text-sm text-slate-600">Selected: {file.name}</p>
                  )}
                </div>

                <Button
                  onClick={handleBulkUpload}
                  disabled={!file || uploading}
                  className="bg-cyan-600 hover:bg-cyan-700"
                  data-testid="upload-button"
                >
                  <Upload className="mr-2 h-4 w-4" />
                  {uploading ? 'Uploading...' : 'Upload Leads'}
                </Button>

                {uploadResults && (
                  <Alert className={uploadResults.error_count > 0 ? 'border-amber-500' : 'border-green-500'}>
                    <AlertDescription>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="h-5 w-5 text-green-600" />
                          <span className="font-medium">Upload Complete!</span>
                        </div>
                        <div className="text-sm space-y-1">
                          <p>✅ Successfully created: {uploadResults.success_count} leads</p>
                          {uploadResults.error_count > 0 && (
                            <p className="text-amber-700">⚠️ Skipped/Failed: {uploadResults.error_count} leads</p>
                          )}
                        </div>
                        {uploadResults.errors && uploadResults.errors.length > 0 && (
                          <div className="mt-3 space-y-3">
                            <p className="text-sm font-medium">Errors:</p>
                            <ul className="text-xs text-slate-600 space-y-1 ml-4 list-disc max-h-40 overflow-y-auto">
                              {uploadResults.errors.slice(0, 10).map((err, idx) => (
                                <li key={idx}>{err}</li>
                              ))}
                              {uploadResults.errors.length > 10 && (
                                <li className="text-amber-600">... and {uploadResults.errors.length - 10} more errors</li>
                              )}
                            </ul>
                            {uploadResults.upload_id && (
                              <Button
                                onClick={() => downloadRejectedLeads(uploadResults.upload_id)}
                                variant="outline"
                                size="sm"
                                className="mt-2"
                              >
                                <Download className="mr-2 h-4 w-4" />
                                Export Rejected Leads
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Meta Ads Tab */}
          <TabsContent value="meta-ads">
            <Card data-testid="meta-ads-card">
              <CardHeader>
                <CardTitle>Meta Lead Ads Integration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <AlertDescription>
                    Connect your Meta (Facebook/Instagram) Lead Ads directly to Real Estate CRM.
                    Leads will be automatically imported and assigned via round-robin.
                  </AlertDescription>
                </Alert>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Webhook URL</Label>
                    <div className="flex gap-2">
                      <Input
                        value={`${BACKEND_URL}/api/webhook/meta`}
                        readOnly
                        className="font-mono text-sm"
                        data-testid="webhook-url-input"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(`${BACKEND_URL}/api/webhook/meta`)}
                        data-testid="copy-webhook-button"
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                    <h3 className="font-semibold text-sm">Setup Instructions:</h3>
                    <ol className="text-sm space-y-2 list-decimal ml-5">
                      <li>Go to Meta Business Suite → Lead Ads</li>
                      <li>Select your Lead Ad form</li>
                      <li>Navigate to "Instant Forms" → "Settings"</li>
                      <li>Under "Lead Access", add a webhook</li>
                      <li>Paste the webhook URL above</li>
                      <li>Method: <code className="bg-white px-2 py-1 rounded">POST</code></li>
                      <li>Subscribe to "leadgen" events</li>
                    </ol>
                  </div>

                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>Note:</strong> Leads from Meta will be automatically assigned to brokers
                      based on available projects. Source will be set to "Facebook".
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* API & Automation Tab */}
          <TabsContent value="api">
            <div className="space-y-4">
              {/* Make.com Card */}
              <Card data-testid="make-integration-card">
                <CardHeader>
                  <CardTitle>Make.com Integration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label>API Endpoint</Label>
                    <div className="flex gap-2">
                      <Input
                        value={`${BACKEND_URL}/api/leads`}
                        readOnly
                        className="font-mono text-sm"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(`${BACKEND_URL}/api/leads`)}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Authorization Header</Label>
                    <div className="flex gap-2">
                      <Input
                        value={`Bearer ${apiKey}`}
                        readOnly
                        className="font-mono text-sm"
                        type="password"
                      />
                      <Button
                        variant="outline"
                        onClick={() => copyToClipboard(`Bearer ${apiKey}`)}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>

                  <div className="bg-slate-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-sm mb-2">Make.com Setup:</h3>
                    <ol className="text-sm space-y-1 list-decimal ml-5">
                      <li>Create new scenario in Make.com</li>
                      <li>Add HTTP module → "Make a request"</li>
                      <li>URL: <code className="bg-white px-1 rounded">{`${BACKEND_URL}/api/leads`}</code></li>
                      <li>Method: POST</li>
                      <li>Headers: <code className="bg-white px-1 rounded">Authorization: Bearer [your-token]</code></li>
                      <li>Body type: JSON</li>
                      <li><strong>Important:</strong> Set <code className="bg-white px-1 rounded">"source"</code> to actual lead source (99acres, Housing.com, etc.)</li>
                    </ol>
                  </div>
                </CardContent>
              </Card>

              {/* Zapier/n8n Card */}
              <Card data-testid="zapier-integration-card">
                <CardHeader>
                  <CardTitle>Zapier / n8n Integration</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Alert>
                    <AlertDescription>
                      Use the same API endpoint and authentication for Zapier and n8n workflows.
                    </AlertDescription>
                  </Alert>

                  <div className="bg-slate-50 p-4 rounded-lg space-y-3">
                    <h3 className="font-semibold text-sm">Request Body (JSON):</h3>
                    <Textarea
                      rows={14}
                      readOnly
                      value={JSON.stringify({
                        customer_name: "John Doe",
                        mobile: "9876543210",
                        email: "john@example.com",
                        projects: ["Aarambh", "Solace"],
                        source: "99acres",
                        sub_source: "Premium Listing",
                        lead_date: "2025-11-15T10:30:00Z"
                      }, null, 2)}
                      className="font-mono text-xs"
                    />
                    <p className="text-xs text-slate-600">
                      <strong>Note:</strong> The system automatically detects entry method as "API" for all API integrations. 
                      The <code className="bg-white px-1 rounded">source</code> field should be the actual lead source (99acres, Housing.com, Facebook, etc.), NOT "Make.com" or "Zapier".
                      <br/><strong>lead_date:</strong> Optional field to set custom lead creation date (format: DD-MM-YYYY or DD/MM/YYYY, e.g., 01-11-2025 = 1st November 2025). If not provided, current timestamp is used.
                    </p>
                  </div>

                  <div className="bg-green-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-sm text-green-800 mb-2">Success Response (201):</h3>
                    <pre className="text-xs bg-white p-2 rounded overflow-x-auto">
{`{
  "id": "uuid",
  "lead_number": "L-0001",
  "customer_name": "John Doe",
  "mobile": "9876543210",
  "assigned_to": "broker-uuid",
  "assigned_to_name": "Amit Singh",
  "lead_status": "New"
}`}
                    </pre>
                  </div>
                </CardContent>
              </Card>

              {/* API Documentation Card */}
              <Card data-testid="api-docs-card">
                <CardHeader>
                  <CardTitle>API Documentation</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-sm mb-2">Available Sources:</h3>
                      <div className="flex flex-wrap gap-2">
                        {['Magicbricks', '99 Acres', 'Facebook', 'Channel Partner', 'Website', 'Walkin', 'Housing.com', 'Reference'].map(source => (
                          <span key={source} className="bg-slate-100 px-2 py-1 rounded text-xs">{source}</span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-amber-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-sm text-amber-800 mb-2">⚠️ Important Notes:</h3>
                      <ul className="text-xs text-amber-700 space-y-1 list-disc ml-5">
                        <li>Mobile numbers must be 7-15 digits and unique</li>
                        <li>At least one project must be specified</li>
                        <li>Duplicate mobile numbers will be rejected</li>
                        <li>Leads are auto-assigned via round-robin based on projects</li>
                        <li>Keep your API token secure and never share publicly</li>
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Project Mapping Tab */}
          <TabsContent value="project-mapping">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Project Name Mapping</CardTitle>
                  <p className="text-sm text-slate-600">
                    Map external project names (from Meta, 99acres, etc.) to your CRM projects
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Add New Mapping */}
                  <div className="space-y-4 p-4 border border-slate-200 rounded-lg bg-slate-50">
                    <h3 className="font-semibold">Add New Mapping</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>External Project Name (from API/99acres/etc.)</Label>
                        <Input
                          placeholder="e.g., Real Estate Aarambh Greens"
                          value={newMapping.external_project_name}
                          onChange={(e) => setNewMapping({ ...newMapping, external_project_name: e.target.value })}
                        />
                        <p className="text-xs text-slate-500">The exact name as it comes from external source</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Map to CRM Project</Label>
                        <select
                          className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          value={newMapping.crm_project_id}
                          onChange={(e) => setNewMapping({ ...newMapping, crm_project_id: e.target.value })}
                        >
                          <option value="">Select Project</option>
                          {projects.map((proj) => (
                            <option key={proj.id} value={proj.id}>
                              {proj.project_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <Button onClick={handleAddMapping} disabled={loadingMappings}>
                      Add Mapping
                    </Button>
                  </div>

                  {/* Existing Mappings */}
                  <div className="space-y-3">
                    <h3 className="font-semibold">Existing Mappings</h3>
                    {mappings.length === 0 ? (
                      <p className="text-sm text-slate-500 py-8 text-center">No mappings created yet</p>
                    ) : (
                      <div className="space-y-2">
                        {mappings.map((mapping) => (
                          <div key={mapping.id} className="flex items-center justify-between p-3 border border-slate-200 rounded-lg hover:bg-slate-50">
                            <div className="flex-1 grid grid-cols-3 gap-4">
                              <div>
                                <p className="text-sm font-medium">{mapping.external_project_name}</p>
                                <p className="text-xs text-slate-500">External Name</p>
                              </div>
                              <div className="flex items-center justify-center">
                                <span className="text-slate-400 text-xl">→</span>
                              </div>
                              <div>
                                <p className="text-sm font-medium text-cyan-600">{mapping.crm_project_name}</p>
                                <p className="text-xs text-slate-500">CRM Project</p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteMapping(mapping.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Delete
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <Alert>
                    <AlertDescription>
                      <div className="space-y-2">
                        <p className="font-medium text-sm">How it works:</p>
                        <ul className="text-xs space-y-1 ml-4 list-disc">
                          <li>When leads come from API, Make.com, 99acres, or any external source, the system automatically checks for project name mappings</li>
                          <li>If a mapping exists, the external project name is instantly converted to your CRM project name</li>
                          <li>This ensures consistency across all leads regardless of source</li>
                          <li>Example: "Real Estate Aarambh Greens" → "Aarambh Greens" automatically</li>
                        </ul>
                      </div>
                    </AlertDescription>
                  </Alert>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
    </div>
  );
}

export default IntegrationsContent;
